import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import {
  ExportCsv,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid"
import type { GridCsvExportOptions } from "@mui/x-data-grid"
import { Box, InputAdornment, Paper, TextField, Tooltip, Typography } from "@mui/material"
import CancelIcon from "@mui/icons-material/Cancel"
import CloudDownloadIcon from "@mui/icons-material/CloudDownload"
import SearchIcon from "@mui/icons-material/Search"

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    title?: string
    csvOptions?: GridCsvExportOptions
    actions?: ReactNode
    actionsPosition?: "start" | "end"
    disableExport?: boolean
    onExportCsv?: () => void
    disableSearch?: boolean
    selectedCount?: number
    selectionActions?: ReactNode
  }
}

interface DataGridToolbarProps {
  title?: string
  csvOptions?: GridCsvExportOptions
  // Table-specific controls. mui-datatables put them after search and download.
  actions?: ReactNode
  actionsPosition?: "start" | "end"
  disableExport?: boolean
  onExportCsv?: () => void
  disableSearch?: boolean
  selectedCount?: number
  selectionActions?: ReactNode
}

// The grid's own toolbar renders its search box through the MUI 7 TextField API
// (`slotProps.input` / `slotProps.htmlInput`), which MUI 5 ignores. This one maps
// the same props onto `InputProps` / `inputProps` until Phase 11 moves the core.
const searchAsPhrase = (input: string) => (input.trim() ? [input.trim()] : [])

const DataGridToolbar = ({
  title,
  csvOptions,
  actions,
  actionsPosition = "end",
  disableExport = false,
  onExportCsv,
  disableSearch = false,
  selectedCount = 0,
  selectionActions,
}: DataGridToolbarProps) => {
  const { t } = useTranslation()
  const toolbarActions = actions && (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ml: "auto",
        flexBasis: { xs: "100%", md: "auto" },
      }}
    >
      {actions}
    </Box>
  )

  return (
    <>
      {/* mui-datatables' selection bar, placed above the toolbar as the old tables set it. */}
      {selectedCount > 0 && (
        <Paper
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            backgroundColor: "background.light",
          }}
        >
          <Typography variant="body2" sx={{ pl: "26px" }}>
            {`${selectedCount} ${t("table.SELECTED_ROWS")}`}
          </Typography>
          {selectionActions}
        </Paper>
      )}
      <Toolbar>
        {/* Below md the title, the table's actions, and search with download each take a line, as
            mui-datatables' toolbar did: on one line, opening the search would push the others down. */}
        <Typography
          variant="h6"
          sx={{
            flex: "1 1 auto",
            flexBasis: { xs: "100%", md: "auto" },
            textAlign: { xs: "center", md: "left" },
            // Without a title it only pushes the controls right on wide screens.
            display: title ? undefined : { xs: "none", md: "block" },
          }}
        >
          {title}
        </Typography>
        {actionsPosition === "start" && toolbarActions}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", md: "flex-end" },
            gap: 1,
            ml: "auto",
            maxWidth: "100%",
            flexBasis: { xs: "100%", md: "auto" },
          }}
        >
          {!disableSearch && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                // Down to the TextField, so a field wider than the screen shrinks instead of overflowing.
                minWidth: 0,
                "& > *": { minWidth: 0 },
                // The field takes the trigger's place; ToolbarButton drops a className of its own.
                "& .search--expanded > button": { display: "none" },
              }}
            >
              <QuickFilter
                className={(state) => (state.expanded ? "search--expanded" : "")}
                // mui-datatables matched the whole phrase; the grid would split it into words that
                // each have to match somewhere in the row.
                parser={searchAsPhrase}
              >
                <Tooltip title={t("table.SEARCH")}>
                  <QuickFilterTrigger render={<ToolbarButton />}>
                    <SearchIcon />
                  </QuickFilterTrigger>
                </Tooltip>
                <QuickFilterControl
                  render={({ ref, slotProps, ...controlProps }, state) => (
                    <TextField
                      {...controlProps}
                      inputRef={ref}
                      inputProps={slotProps?.htmlInput}
                      aria-label={t("table.SEARCH")}
                      placeholder={t("table.SEARCH")}
                      size="small"
                      // The theme gives every FormControl a 1rem bottom margin, which lifts the field off-center.
                      sx={{
                        width: 260,
                        maxWidth: "100%",
                        minWidth: 0,
                        m: 0,
                        display: state.expanded ? undefined : "none",
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: state.value ? (
                          <InputAdornment position="end">
                            <QuickFilterClear edge="end" size="small">
                              <CancelIcon fontSize="small" />
                            </QuickFilterClear>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  )}
                />
              </QuickFilter>
            </Box>
          )}
          {!disableExport && (
            <Tooltip title={t("table.DOWNLOAD_CSV")}>
              {onExportCsv ? (
                <ToolbarButton onClick={onExportCsv}>
                  <CloudDownloadIcon />
                </ToolbarButton>
              ) : (
                <ExportCsv render={<ToolbarButton />} options={csvOptions}>
                  <CloudDownloadIcon />
                </ExportCsv>
              )}
            </Tooltip>
          )}
        </Box>
        {actionsPosition === "end" && toolbarActions}
      </Toolbar>
    </>
  )
}

export default DataGridToolbar
